import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorScreen } from "./ErrorScreen";

interface Props {
  children: ReactNode;
  /** Opcjonalny custom fallback */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const RELOAD_FLAG = "lov_chunk_reload_at";

/**
 * Sprawdza, czy błąd to typowy „chunk load error" — pojawia się, gdy po deployu
 * przeglądarka (zwłaszcza mobilna) ma w cache stary index.html odwołujący się do
 * nieistniejących już hashy plików JS. Standardowa naprawa: jednorazowe twarde
 * przeładowanie strony.
 */
const isChunkLoadError = (err: unknown): boolean => {
  if (!err) return false;
  const message = err instanceof Error ? err.message : String(err);
  const name = err instanceof Error ? err.name : "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
};

/**
 * Top-level ErrorBoundary łapiący nieobsłużone wyjątki w drzewie React.
 * Zapobiega białemu ekranowi — pokazuje czytelny ErrorScreen z możliwością
 * ponownego załadowania strony.
 *
 * Specjalna obsługa „chunk load error": jednorazowy twardy reload (anti-loop
 * przez sessionStorage), żeby telefon pobrał świeży index.html z aktualnymi
 * hashami chunków po deployu.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AppErrorBoundary]", error, info.componentStack);

    if (isChunkLoadError(error)) {
      try {
        const last = Number(sessionStorage.getItem(RELOAD_FLAG) || "0");
        const now = Date.now();
        // Reload tylko raz na 30s, żeby nie wpaść w pętlę
        if (now - last > 30_000) {
          sessionStorage.setItem(RELOAD_FLAG, String(now));
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      const chunkErr = isChunkLoadError(this.state.error);
      return (
        <ErrorScreen
          title={
            chunkErr
              ? "Pobieram nową wersję aplikacji…"
              : "Wystąpił nieoczekiwany błąd"
          }
          description={
            chunkErr
              ? "Wygląda na to, że dostępna jest nowa wersja strony. Za chwilę odświeżymy ją automatycznie. Jeśli się nie odświeży, kliknij przycisk poniżej."
              : "Aplikacja napotkała problem i nie może kontynuować. Spróbuj odświeżyć stronę."
          }
          details={this.state.error?.stack || this.state.error?.message}
        />
      );
    }
    return this.props.children;
  }
}
